import axios from "axios";
import { ApiLink } from "../../../../APILink/ApiLink";
import authHeader from "../../../../Auth/authHeader";

const api = axios.create({
  baseURL: ApiLink.API_URL,
  headers: authHeader()
});

const safeNum = (value) => (typeof value === "number" && !Number.isNaN(value) ? value : Number(value) || 0);
const normalizeFiscalYear = (value) => {
  const parsed = Number(value);
  if (Number.isInteger(parsed) && parsed > 0) return parsed;
  return new Date().getFullYear();
};
const isNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

const normalizeDate = (value) => {
  if (!value) return "";
  const text = String(value);
  if (text.includes("T")) return text.split("T")[0];
  if (text.includes(" ")) return text.split(" ")[0];
  return text;
};

const toRangeParams = (dateRange) => {
  const params = {};
  if (dateRange?.from) params.fromDate = dateRange.from;
  if (dateRange?.to) params.toDate = dateRange.to;
  return params;
};

const mapFinancialRows = (rows, openingBalance = 0) => {
  let runningBalance = openingBalance;

  return (rows || []).map((row, index) => {
    const debit = safeNum(row.debit ?? row.Debit);
    const credit = safeNum(row.credit ?? row.Credit);
    runningBalance += debit - credit;

    return {
      id: row.id ?? row.JournalID ?? index + 1,
      journalID: row.journalID ?? row.JournalID ?? 0,
      date: row.date ?? row.Date ?? "",
      documentNo: row.documentNo ?? row.DocumentNo ?? row.journalID ?? row.JournalID ?? "—",
      accountName:
        row.accountName ??
        row.AccountName ??
        row.accName ??
        row.AccName ??
        "",
      identificationNumber:
        row.identificationNumber ??
        row.IdentificationNumber ??
        row.idNumber ??
        row.IdNumber ??
        row.NUI ??
        row.nui ??
        "",
      actionType: row.actionType ?? row.ActionType ?? row.type ?? row.Type ?? "",
      description: row.description ?? row.Comment ?? row.comment ?? "",
      debit,
      credit,
      currency: row.currency ?? row.Currency ?? "ALL",
      status: row.status ?? row.ActionType ?? (credit > 0 ? "paid" : "open"),
      runningBalance
    };
  });
};

export async function fetchClientAccounts({ ledgerID = 105, search = "", page = 1, pageSize = 50 } = {}) {
  let response;
  try {
    response = await api.get("Accounts/accountByLedger", {
      params: { ledger: ledgerID }
    });
  } catch {
    response = await api.get("Accounts/accountsPick", {
      params: { ledgerID, search, page, pageSize }
    });
  }

  return (response.data || []).map((item) => ({
    id: item.AccountID ?? item.accountID ?? item.AccountId ?? item.accountId ?? item.id,
    AccountID: item.AccountID ?? item.accountID ?? item.AccountId ?? item.accountId ?? item.id,
    AccountName: item.AccountName ?? item.accountName ?? "",
    IdentificationNumber: item.IdentificationNumber ?? item.identificationNumber ?? "",
    VATNumber: item.VATNumber ?? item.vatNumber ?? "",
    Adress: item.Adress ?? item.adress ?? "",
    LedgerNameEN: item.LedgerNameEN ?? item.ledgerNameEN ?? "",
    IsActive: item.IsActive ?? item.isActive ?? true
  }));
}

export async function listFinancialTransactionsPage(req) {
  const fiscalYear = normalizeFiscalYear(req.fiscalYear);
  try {
    const response = await api.get("ClientCard/financial/dashboard", {
      params: {
        accountID: req.clientId,
        fiscalYear,
        page: req.page || 1,
        pageSize: req.pageSize || 25,
        sortKey: req.sortKey || "date",
        sortDir: req.sortDir || "desc",
        search: req.filters?.search || "",
        status: req.filters?.status || "all",
        currency: req.filters?.currency || "",
        ...toRangeParams(req.filters?.dateRange)
      }
    });

    const rows = mapFinancialRows(response.data?.rows || [], response.data?.summary?.openingBalance || 0);
    const total = response.data?.total ?? response.data?.totalRows ?? rows.length;
    if (rows.length > 0 || total > 0) {
      return { rows, total };
    }
  } catch {
    // Fall back to legacy Card endpoints used by KartelaKlientit.
  }

  const legacyResponse = await api.get("Card/getKartelaFinanciareAnalitike", {
    params: {
      fiscalYear,
      accountID: req.clientId
    }
  });

  const legacyRows = mapFinancialRows(legacyResponse.data || [], 0);
  const page = req.page || 1;
  const pageSize = req.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    rows: legacyRows.slice(start, end),
    total: legacyRows.length
  };
}

export async function computeFinancialKpis(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/financial/dashboard", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    if (response.data?.summary) {
      return response.data.summary;
    }
  } catch {
    // Fall back to legacy computation.
  }

  const legacyResponse = await api.get("Card/getKartelaFinanciareAnalitike", {
    params: {
      fiscalYear: normalizedFiscalYear,
      accountID: clientId
    }
  });

  const legacyRows = legacyResponse.data || [];
  const totalDebit = legacyRows.reduce((sum, row) => sum + safeNum(row.Debit ?? row.debit), 0);
  const totalCredit = legacyRows.reduce((sum, row) => sum + safeNum(row.Credit ?? row.credit), 0);
  const currentBalance = totalDebit - totalCredit;

  return {
    openingBalance: 0,
    totalDebit,
    totalCredit,
    currentBalance,
    overdueAmount: 0,
    paymentTermsDays: 30,
    lastTransactionDate: legacyRows?.[0]?.Date || legacyRows?.[0]?.date || null,
    paidVsUnpaid: {
      paid: legacyRows.filter((row) => safeNum(row.Credit ?? row.credit) > 0).length,
      unpaid: legacyRows.filter((row) => safeNum(row.Credit ?? row.credit) <= 0).length
    }
  };
}

export async function getFinancialChartSeries(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/financial/dashboard", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const balanceOverTime = response.data?.balanceOverTime || [];
    const monthlyMovement = response.data?.monthlyMovement || [];
    if (isNonEmptyArray(balanceOverTime) || isNonEmptyArray(monthlyMovement)) {
      return {
        balanceOverTime,
        monthlyMovement
      };
    }
  } catch {
    // Fall back to legacy chart sources.
  }

  const [legacyMonthlyResponse, legacyRowsResponse] = await Promise.all([
    api.get("Card/getDebitAndCreditForClients", {
      params: {
        year: normalizedFiscalYear,
        accountID: clientId
      }
    }),
    api.get("Card/getKartelaFinanciareAnalitike", {
      params: {
        fiscalYear: normalizedFiscalYear,
        accountID: clientId
      }
    })
  ]);

  const monthlyMovement = (legacyMonthlyResponse.data || []).map((item) => ({
    month: String(item.MonthNumber || item.month || ""),
    debit: safeNum(item["Te arketueshme"] ?? item.debit),
    credit: safeNum(item.Arketimet ?? item.credit)
  }));

  let balance = 0;
  const balanceOverTime = [...(legacyRowsResponse.data || [])]
    .reverse()
    .map((item) => {
      balance += safeNum(item.Debit ?? item.debit) - safeNum(item.Credit ?? item.credit);
      return {
        date: normalizeDate(item.Date ?? item.date),
        balance
      };
    });

  return {
    balanceOverTime,
    monthlyMovement
  };
}

export async function computeMaterialSynthetic(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/material/synthetic", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const salesSummary = response.data?.salesSummary || response.data?.SalesSummary;
    if (salesSummary) {
      return {
        totalSoldQty: safeNum(salesSummary.totalSoldQty ?? salesSummary.TotalSoldQty),
        totalReturnedQty: safeNum(salesSummary.totalReturnedQty ?? salesSummary.TotalReturnedQty),
        netSoldQty: safeNum(salesSummary.netSoldQty ?? salesSummary.NetSoldQty),
        totalSalesValue: safeNum(salesSummary.totalSalesValue ?? salesSummary.TotalSalesValue),
        averageUnitSalesValue: safeNum(salesSummary.averageUnitSalesValue ?? salesSummary.AverageUnitSalesValue),
        distinctSoldArticles: Number((salesSummary.distinctSoldArticles ?? salesSummary.DistinctSoldArticles) || 0),
        activeSalesMonths: Number((salesSummary.activeSalesMonths ?? salesSummary.ActiveSalesMonths) || 0),
        monthlyAverageSoldQty: safeNum(salesSummary.monthlyAverageSoldQty ?? salesSummary.MonthlyAverageSoldQty)
      };
    }

    const summary = response.data?.summary;
    if (summary) return summary;
  } catch {
    // Fall back to legacy source.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear: normalizedFiscalYear,
      accountID: clientId
    }
  });

  const rows = legacyResponse.data || [];
  const totalSoldQty = rows.reduce((sum, item) => sum + safeNum(item.TotalQtyOutput), 0);
  const totalSalesValue = rows.reduce((sum, item) => sum + safeNum(item.TotalAmount), 0);
  const averageUnitSalesValue = totalSoldQty > 0 ? totalSalesValue / totalSoldQty : 0;
  const distinctSoldArticles = rows.reduce((count, item) => count + (safeNum(item.TotalQtyOutput) > 0 ? 1 : 0), 0);

  return {
    totalSoldQty,
    totalReturnedQty: 0,
    netSoldQty: totalSoldQty,
    totalSalesValue,
    averageUnitSalesValue,
    distinctSoldArticles,
    activeSalesMonths: 0,
    monthlyAverageSoldQty: 0
  };
}

export async function getMaterialSyntheticSeries(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  const normalizeSeriesPoint = (item) => ({
    date: item?.date ?? item?.Date ?? "",
    stock: safeNum(item?.stock ?? item?.Stock),
    soldQtyCumulative: safeNum(item?.soldQtyCumulative ?? item?.SoldQtyCumulative),
    salesValueCumulative: safeNum(item?.salesValueCumulative ?? item?.SalesValueCumulative)
  });

  const normalizeMonthlyPoint = (item) => ({
    month: item?.month ?? item?.Month ?? "",
    soldQty: safeNum(item?.soldQty ?? item?.SoldQty ?? item?.outQty ?? item?.OutQty),
    returnedQty: safeNum(item?.returnedQty ?? item?.ReturnedQty ?? item?.inQty ?? item?.InQty),
    salesValue: safeNum(item?.salesValue ?? item?.SalesValue ?? item?.invValue ?? item?.InvValue),
    outQty: safeNum(item?.outQty ?? item?.OutQty ?? item?.soldQty ?? item?.SoldQty),
    inQty: safeNum(item?.inQty ?? item?.InQty ?? item?.returnedQty ?? item?.ReturnedQty),
    invValue: safeNum(item?.invValue ?? item?.InvValue ?? item?.salesValue ?? item?.SalesValue)
  });

  const toIsoDate = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

    const dashDmy = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashDmy) return `${dashDmy[3]}-${dashDmy[2]}-${dashDmy[1]}`;

    const slashDmy = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashDmy) return `${slashDmy[3]}-${slashDmy[2]}-${slashDmy[1]}`;

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const buildSeriesFromLegacy = async () => {
    const syntheticResp = await api.get("Card/getKartelaMaterialeSintetike", {
      params: {
        fiscalYear: normalizedFiscalYear,
        accountID: clientId
      }
    });

    const syntheticRows = Array.isArray(syntheticResp.data) ? syntheticResp.data : [];
    if (!syntheticRows.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const articleIds = syntheticRows
      .map((item) => Number(item?.ArticleID || item?.articleID || 0))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (!articleIds.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const historyResponses = await Promise.all(
      articleIds.map((articleId) =>
        api.get("Card/getKartelaMaterialeAnalitike", {
          params: {
            fiscalYear: normalizedFiscalYear,
            accountID: clientId,
            articleID: articleId
          }
        })
      )
    );

    const historyRows = historyResponses.flatMap((resp) => (Array.isArray(resp.data) ? resp.data : []));
    if (!historyRows.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const normalizedHistory = historyRows
      .map((row) => ({
        date: toIsoDate(row?.Date ?? row?.date),
        soldQty: safeNum(row?.QtyOutput ?? row?.qtyOutput),
        salesValue: safeNum(row?.TotalAmount ?? row?.totalAmount)
      }))
      .filter((row) => row.date)
      .sort((a, b) => a.date.localeCompare(b.date));

    if (!normalizedHistory.length) {
      return { stockOverTime: [], salesOverTime: [], monthly: [] };
    }

    const salesOverTime = [];
    let soldCum = 0;
    let valueCum = 0;
    normalizedHistory.forEach((row) => {
      soldCum += row.soldQty;
      valueCum += row.salesValue;
      salesOverTime.push({
        date: row.date,
        stock: 0,
        soldQtyCumulative: soldCum,
        salesValueCumulative: valueCum
      });
    });

    const monthlyMap = new Map();
    normalizedHistory.forEach((row) => {
      const month = row.date.slice(0, 7);
      if (!monthlyMap.has(month)) {
        monthlyMap.set(month, {
          month,
          soldQty: 0,
          returnedQty: 0,
          salesValue: 0,
          outQty: 0,
          inQty: 0,
          invValue: 0
        });
      }
      const bucket = monthlyMap.get(month);
      bucket.soldQty += row.soldQty;
      bucket.outQty += row.soldQty;
      bucket.salesValue += row.salesValue;
      bucket.invValue += row.salesValue;
    });

    const monthly = Array.from(monthlyMap.values()).sort((a, b) => a.month.localeCompare(b.month));

    return {
      stockOverTime: salesOverTime,
      salesOverTime,
      monthly
    };
  };

  try {
    const response = await api.get("ClientCard/material/synthetic", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const rawSalesOverTime = response.data?.salesOverTime || response.data?.SalesOverTime || [];
    const rawStockOverTime = response.data?.stockOverTime || response.data?.StockOverTime || [];
    const rawMonthly = response.data?.monthly || response.data?.Monthly || [];

    const salesOverTime = (rawSalesOverTime || []).map(normalizeSeriesPoint);
    const stockOverTime = (rawStockOverTime || []).map(normalizeSeriesPoint);
    const monthly = (rawMonthly || []).map(normalizeMonthlyPoint);

    if (isNonEmptyArray(salesOverTime) || isNonEmptyArray(monthly)) {
      return {
        stockOverTime,
        salesOverTime,
        monthly
      };
    }

    if (isNonEmptyArray(stockOverTime) || isNonEmptyArray(monthly)) {
      return {
        stockOverTime,
        salesOverTime: stockOverTime,
        monthly
      };
    }
  } catch {
    try {
      return await buildSeriesFromLegacy();
    } catch {
      // Ignore and return empty below.
    }
  }

  try {
    return await buildSeriesFromLegacy();
  } catch {
    return {
      stockOverTime: [],
      salesOverTime: [],
      monthly: []
    };
  }
}

export async function listMaterialSyntheticRowsPage(req) {
  const fiscalYear = normalizeFiscalYear(req?.fiscalYear);

  const mapRows = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? index + 1,
      articleId: Number(item.articleId ?? item.ArticleID ?? 0),
      barcode: item.barcode ?? item.Barcode ?? "",
      articleName: item.articleName ?? item.ArticleName ?? "",
      totalTransactions: safeNum(item.totalTransactions ?? item.TotalTransactions),
      totalQtyOutput: safeNum(item.totalQtyOutput ?? item.TotalQtyOutput),
      avgPriceWithoutVAT: safeNum(item.avgPriceWithoutVAT ?? item.AvgPriceWithoutVAT),
      totalAmount: safeNum(item.totalAmount ?? item.TotalAmount),
      rowInfo: item
    }));

  try {
    const response = await api.get("ClientCard/material/synthetic/rows", {
      params: {
        accountID: req.clientId,
        fiscalYear,
        page: req.page || 1,
        pageSize: req.pageSize || 25,
        search: req.filters?.search || "",
        sort: req.sort || "",
        ...toRangeParams(req.filters?.dateRange)
      }
    });

    const rows = mapRows(response.data?.rows || response.data?.Rows || response.data || []);
    const total = Number(response.data?.totalRows ?? response.data?.TotalRows ?? rows.length);
    if (rows.length > 0 || total > 0) {
      return { rows, total };
    }
  } catch {
    // Fall back to legacy list from Card/getKartelaMaterialeSintetike.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear,
      accountID: req.clientId
    }
  });

  const legacyRows = mapRows(legacyResponse.data || []);
  const searchTerm = String(req.filters?.search || "").trim().toLowerCase();
  const searched = searchTerm
    ? legacyRows.filter((row) => `${row.barcode} ${row.articleName}`.toLowerCase().includes(searchTerm))
    : legacyRows;

  const page = req.page || 1;
  const pageSize = req.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    rows: searched.slice(start, end),
    total: searched.length
  };
}

export async function computeMaterialAnalytical(clientId, opts) {
  const fiscalYear = normalizeFiscalYear(opts?.fiscalYear);
  const mapAnalyticalRows = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? index + 1,
      itemId: item.itemId ?? item.ArticleID ?? item.articleID ?? index + 1,
      itemCode: item.itemCode ?? item.Barcode ?? "",
      itemName: item.itemName ?? item.ArticleName ?? "",
      category: item.category ?? item.ArticleGroupName ?? "",
      unit: item.unit ?? item.ArticleUnitName ?? "",
      warehouse: item.warehouse ?? item.WarehouseName ?? "",
      qtyIn: safeNum(item.qtyIn ?? item.QtyIn),
      qtyOut: safeNum(item.qtyOut ?? item.TotalQtyOutput ?? item.QtyOutput),
      balance: safeNum(item.balance ?? item.Balance),
      unitCostAvg: safeNum(item.unitCostAvg ?? item.AvgPriceWithoutVAT ?? item.UnitCostAvg),
      totalValue: safeNum(item.totalValue ?? item.TotalAmount ?? item.TotalValue)
    }));

  try {
    const response = await api.get("ClientCard/material/analytical", {
      params: {
        accountID: clientId,
        fiscalYear,
        search: opts?.search || "",
        stockStatus: opts?.stockStatus || "all",
        warehouse: opts?.warehouse || "",
        category: opts?.category || "",
        ...toRangeParams(opts?.dateRange)
      }
    });

    const rows = mapAnalyticalRows(response.data?.rows || response.data?.items || response.data || []);
    if (rows.length > 0) return rows;
  } catch {
    // Fall back to legacy material synthetic source for per-item analytical list.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear,
      accountID: clientId
    }
  });

  return mapAnalyticalRows(legacyResponse.data || []);
}

export async function listMaterialAnalyticalRowsPage(req) {
  const fiscalYear = normalizeFiscalYear(req?.fiscalYear);
  const mapRows = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? index + 1,
      itemId: item.itemId ?? item.ArticleID ?? item.articleID ?? index + 1,
      itemCode: item.itemCode ?? item.Barcode ?? "",
      itemName: item.itemName ?? item.ArticleName ?? "",
      category: item.category ?? item.ArticleGroupName ?? "",
      unit: item.unit ?? item.ArticleUnitName ?? "",
      warehouse: item.warehouse ?? item.WarehouseName ?? "",
      qtyIn: safeNum(item.qtyIn ?? item.QtyIn),
      qtyOut: safeNum(item.qtyOut ?? item.TotalQtyOutput ?? item.QtyOutput),
      balance: safeNum(item.balance ?? item.Balance),
      unitCostAvg: safeNum(item.unitCostAvg ?? item.AvgPriceWithoutVAT ?? item.UnitCostAvg),
      totalValue: safeNum(item.totalValue ?? item.TotalAmount ?? item.TotalValue),
      rowInfo: item
    }));

  try {
    const response = await api.get("ClientCard/material/analytical/rows", {
      params: {
        accountID: req.clientId,
        fiscalYear,
        page: req.page || 1,
        pageSize: req.pageSize || 25,
        sort: req.sort || "",
        filterModel: JSON.stringify(req.filterItems || []),
        search: req.filters?.search || "",
        category: req.filters?.category || "",
        stockStatus: req.filters?.stockStatus || "all",
        ...toRangeParams(req.filters?.dateRange)
      }
    });

    const rows = mapRows(response.data?.rows || response.data?.Rows || []);
    const total = Number(response.data?.totalRows ?? response.data?.TotalRows ?? rows.length);
    if (rows.length > 0 || total > 0) {
      return { rows, total };
    }
  } catch {
    // fall back below
  }

  const allRows = await computeMaterialAnalytical(req.clientId, {
    dateRange: req.filters?.dateRange,
    fiscalYear,
    search: req.filters?.search || "",
    category: req.filters?.category || "",
    stockStatus: req.filters?.stockStatus || "all"
  });

  const page = req.page || 1;
  const pageSize = req.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    rows: mapRows(allRows).slice(start, end),
    total: (allRows || []).length
  };
}

export async function getMaterialAnalyticalCharts(clientId, opts = {}) {
  const fiscalYear = normalizeFiscalYear(opts?.fiscalYear);

  try {
    const response = await api.get("ClientCard/material/analytical/charts", {
      params: {
        accountID: clientId,
        fiscalYear,
        search: opts?.search || "",
        stockStatus: opts?.stockStatus || "all",
        category: opts?.category || "",
        ...toRangeParams(opts?.dateRange)
      }
    });

    const data = response.data || {};
    return {
      topTraded: data.topTraded || data.TopTraded || [],
      topBySalesValue: data.topBySalesValue || data.TopBySalesValue || [],
      monthlyTrend: data.monthlyTrend || data.MonthlyTrend || [],
      categoryDistribution: data.categoryDistribution || data.CategoryDistribution || []
    };
  } catch {
    const legacy = await getTopMaterialInsights(clientId, opts?.dateRange, fiscalYear);

    const [analyticalRows, syntheticSeries] = await Promise.all([
      computeMaterialAnalytical(clientId, {
        dateRange: opts?.dateRange,
        fiscalYear,
        search: opts?.search,
        category: opts?.category,
        stockStatus: opts?.stockStatus
      }),
      getMaterialSyntheticSeries(clientId, opts?.dateRange, fiscalYear)
    ]);

    const categoryMap = new Map();
    (analyticalRows || []).forEach((row) => {
      const categoryName = String(row?.category || "Pa kategori").trim() || "Pa kategori";
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          category: categoryName,
          soldQty: 0,
          salesValue: 0
        });
      }
      const bucket = categoryMap.get(categoryName);
      bucket.soldQty += safeNum(row?.qtyOut);
      bucket.salesValue += safeNum(row?.totalValue);
    });

    const categoryDistribution = Array.from(categoryMap.values())
      .sort((a, b) => b.salesValue - a.salesValue)
      .slice(0, 10);

    const monthlyTrend = (syntheticSeries?.monthly || []).map((item) => ({
      month: item?.month ?? item?.Month ?? "",
      soldQty: safeNum(item?.soldQty ?? item?.SoldQty),
      salesValue: safeNum(item?.salesValue ?? item?.SalesValue),
      transactions: 0
    }));

    return {
      topTraded: legacy?.topTraded || [],
      topBySalesValue: legacy?.mostProfitable || [],
      monthlyTrend,
      categoryDistribution
    };
  }
}

export async function getTopMaterialInsights(clientId, dateRange, fiscalYear) {
  const normalizedFiscalYear = normalizeFiscalYear(fiscalYear);
  try {
    const response = await api.get("ClientCard/material/analytical", {
      params: {
        accountID: clientId,
        fiscalYear: normalizedFiscalYear,
        ...toRangeParams(dateRange)
      }
    });

    const topTraded = response.data?.topTraded || [];
    const mostProfitable = response.data?.mostProfitable || [];
    if (isNonEmptyArray(topTraded) || isNonEmptyArray(mostProfitable)) {
      return { topTraded, mostProfitable };
    }
  } catch {
    // Fall back to insights from legacy synthetic list.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeSintetike", {
    params: {
      fiscalYear: normalizedFiscalYear,
      accountID: clientId
    }
  });

  const rows = (legacyResponse.data || []).map((item) => ({
    code: item.Barcode || "",
    name: item.ArticleName || "",
    qty: safeNum(item.TotalQtyOutput),
    profit: safeNum(item.TotalAmount),
    itemId: item.ArticleID
  }));

  return {
    topTraded: [...rows].sort((a, b) => b.qty - a.qty).slice(0, 10),
    mostProfitable: [...rows].sort((a, b) => b.profit - a.profit).slice(0, 10)
  };
}

export async function getMaterialAnalyticalHistory(clientId, articleId, opts = {}) {
  const fiscalYear = normalizeFiscalYear(opts?.fiscalYear);
  const mapHistory = (source) =>
    (source || []).map((item, index) => ({
      id: item.id ?? item.HistoryID ?? index + 1,
      date: item.date ?? item.Date ?? "",
      documentNo: item.documentNo ?? item.JournalID ?? item.HistoryID ?? "",
      type: item.type ?? item.ActionType ?? "",
      quantity: safeNum(item.quantity ?? item.QtyOutput ?? item.QtyInput),
      qtyInput: safeNum(item.qtyInput ?? item.QtyInput),
      qtyOutput: safeNum(item.qtyOutput ?? item.QtyOutput),
      unitCost: safeNum(item.unitCost ?? item.PriceWithoutVAT ?? item.PriceWithVAT),
      totalAmount: safeNum(item.totalAmount ?? item.TotalAmount),
      barcode: item.barcode ?? item.Barcode ?? "",
      articleName: item.articleName ?? item.ArticleName ?? ""
    }));

  try {
    const response = await api.get("ClientCard/material/analytical/history", {
      params: {
        accountID: clientId,
        articleID: articleId,
        fiscalYear,
        ...toRangeParams(opts?.dateRange)
      }
    });

    const rows = mapHistory(response.data?.items || response.data || []);
    if (rows.length > 0) return rows;
  } catch {
    // Fall back to legacy history endpoint.
  }

  const legacyResponse = await api.get("Card/getKartelaMaterialeAnalitike", {
    params: {
      fiscalYear,
      accountID: clientId,
      articleID: articleId
    }
  });

  return mapHistory(legacyResponse.data || []);
}

export { normalizeDate };
